import React from "react";
import PropTypes from "prop-types";
import BaseBlock from "./BaseBlock";
import FlexTable from "../../../components/ui-components/FlexTable";
import { fmtEurSigned } from "./format";
import { renderNarrative, hasFigToken } from "../metrics/renderNarrative";

// TONE_CLASS mirrors KPIStripBlock/PerformanceTableBlock's mapping of the
// adverse/favorable/neutral vocabulary (backend_go internal/boardfigures/kpi.go's
// varianceTone) to a colour class: a driver that made spend worse (impact > 0) is
// adverse (red), one that helped (impact < 0) is favorable (green).
const TONE_CLASS = {
  adverse: "a4-kpi-tone-adverse",
  favorable: "a4-kpi-tone-favorable",
  neutral: "a4-kpi-tone-neutral",
};

function impactTone(impact) {
  if (impact > 0) return "adverse";
  if (impact < 0) return "favorable";
  return "neutral";
}

// CATEGORY_LABELS - display copy for the two categories SP2's DecomposePVM currently emits
// (internal/boardfigures/pvm.go, Task 4's per-driver Volume/Rate split, seeded onto the
// driver annotation at run-creation time). Deliberately not the full new/dropped/one_off
// bucket vocabulary from the waterfall's `kind` field - those are page-level waterfall
// buckets, not a value this per-driver `category` field takes on today - but categoryLabel's
// generic title-case fallback below covers them (and anything else) gracefully if that ever
// changes, without a code change here.
const CATEGORY_LABELS = { volume: "Volume", rate: "Rate" };

// CATEGORY_TONE_CLASS - mirrors VarianceWaterfallChart's VOLUME_COLOR/RATE_COLOR two-tone
// family (an orthogonal axis to adverse/favorable, so it deliberately doesn't reuse
// TONE_CLASS) so a reader sees the same Volume/Rate colour language on the driver table as on
// the waterfall chart above it.
const CATEGORY_TONE_CLASS = {
  volume: "a4-driver-category-volume",
  rate: "a4-driver-category-rate",
};

// categoryLabel - "volume"/"rate" -> their display copy; any other non-empty code (a future
// new/dropped/one_off bucket landing on a driver, or an unforeseen value) -> a generic
// title-case fallback ("one_off" -> "One Off") rather than showing the raw snake_case code;
// empty/missing -> null, so the Cell renderer falls back to the file's "—" convention.
function categoryLabel(category) {
  if (!category) return null;
  if (CATEGORY_LABELS[category]) return CATEGORY_LABELS[category];
  return category
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// buildColumns - a factory (rather than a module-level constant) so the free-text columns'
// Cell renderers can close over the current `metrics` map and resolve any `{{fig:KEY}}` @figure
// token (SP6) each of those author-entered fields may contain, via renderNarrative. A cell with
// no token renders exactly as before (renderNarrative returns the plain string verbatim).
function buildColumns(metrics) {
  return [
    { Header: "Driver", accessor: "driver" },
    {
      Header: "Impact",
      accessor: "impact",
      align: "right",
      Cell: ({ value }) => (
        <span className={TONE_CLASS[impactTone(value)]}>{fmtEurSigned(value)}</span>
      ),
    },
    {
      Header: "Category",
      accessor: "category",
      Cell: ({ value }) => {
        const label = categoryLabel(value);
        if (!label) return "—";
        return (
          <span
            className={`a4-driver-category-pill ${
              CATEGORY_TONE_CLASS[value] || "a4-driver-category-other"
            }`}
          >
            {label}
          </span>
        );
      },
    },
    {
      Header: "Cause",
      accessor: "cause",
      Cell: ({ value }) => (value ? renderNarrative(value, metrics) : "—"),
    },
    {
      Header: "Expected",
      accessor: "expected",
      Cell: ({ value }) => (value ? renderNarrative(value, metrics) : "—"),
    },
    {
      Header: "Owner",
      accessor: "owner",
      Cell: ({ value }) => (value ? renderNarrative(value, metrics) : "—"),
    },
    {
      Header: "Action",
      accessor: "action",
      Cell: ({ value }) => (value ? renderNarrative(value, metrics) : "—"),
    },
  ];
}

// rowId - driver labels are the natural key (one row per named waterfall driver,
// backend-seeded, see seedDriverAnnotations), but fall back to the row's index for
// safety since react-table needs a stable, non-empty id even for a blank/duplicate
// author-entered label.
function rowId(row, index) {
  return row.driver || `driver-${index}`;
}

/**
 * DriverTableBlock - the run's FROZEN driver annotations (driver_table block type):
 * Driver/Impact/Category/Cause/Expected/Owner/Action, one row per named MoM variance-waterfall
 * driver (Task 4) augmented with author judgment (Task 8).
 *
 * NOT a fetching block: driver annotations are frozen onto the run at creation/edit
 * time (backend_go cmd/api/board_report_drivers.go's DriverAnnotation - snake_case
 * json tags {driver, impact, cause, expected, owner, action, category}), stored on
 * BoardReportRun.Drivers, and injected here by ManifestPage (Task 18) via the
 * `drivers` prop - never fetched independently. Read-only: editing driver annotations
 * happens on the review screen (Task 19, PUT .../drivers), not in the printed report.
 * `category` (SP2 Task 4/7) is computed by DecomposePVM, not author-editable - it stays
 * read-only here and on the review screen.
 *
 * SP6: cause/expected/owner/action may contain `{{fig:KEY}}` @figure tokens, rendered live via
 * renderNarrative from the optional `metrics` prop (see buildColumns above). When ANY row's
 * free-text field contains a token, `BaseBlock`'s `isLoading` is gated on `metricsLoading` so
 * the PDF poller waits; a token-free driver table keeps `isLoading={false}` (unchanged).
 */
const DriverTableBlock = ({
  title = "Key drivers",
  drivers = [],
  metrics,
  metricsLoading = false,
  onSelectDriver,
  selectedDriverIndex = null,
}) => {
  const rows = Array.isArray(drivers) ? drivers : [];
  const columns = buildColumns(metrics);

  const isLoading =
    rows.some(
      (row) =>
        hasFigToken(row?.cause) ||
        hasFigToken(row?.expected) ||
        hasFigToken(row?.owner) ||
        hasFigToken(row?.action)
    ) && metricsLoading;

  // Per-row selection (review screen only): when ManifestPage passes onSelectDriver, each row is
  // clickable and opens JUST that driver in the inspector. stopPropagation keeps the click from
  // bubbling to the block-selection wrapper (which would re-select the whole block and drop the
  // driverIndex). Absent onSelectDriver (print path + non-selectable preview) → no onRowClick, so
  // the table renders byte-identical to before.
  const selectable = typeof onSelectDriver === "function";
  const handleRowClick = selectable
    ? (id, original, e) => {
        if (e && typeof e.stopPropagation === "function") e.stopPropagation();
        onSelectDriver(rows.indexOf(original));
      }
    : undefined;
  const selectedRowId =
    selectedDriverIndex != null && rows[selectedDriverIndex]
      ? rowId(rows[selectedDriverIndex], selectedDriverIndex)
      : null;

  return (
    <BaseBlock title={title} isLoading={isLoading} error={null} className="a4-driver-table">
      {rows.length === 0 ? (
        <p className="a4-driver-table-empty">No drivers recorded for this period.</p>
      ) : (
        <FlexTable
          columns={columns}
          data={rows}
          getRowId={(row) => rowId(row, rows.indexOf(row))}
          onRowClick={handleRowClick}
          selectedRowId={selectedRowId}
          rowAriaLabel={selectable ? (row) => `Open driver ${row.driver || ""}` : undefined}
        />
      )}
    </BaseBlock>
  );
};

DriverTableBlock.propTypes = {
  title: PropTypes.string,
  drivers: PropTypes.arrayOf(
    PropTypes.shape({
      driver: PropTypes.string,
      impact: PropTypes.number,
      cause: PropTypes.string,
      expected: PropTypes.string,
      owner: PropTypes.string,
      action: PropTypes.string,
      category: PropTypes.string,
    })
  ),
  metrics: PropTypes.object,
  metricsLoading: PropTypes.bool,
  // Review-screen per-row selection (optional): onSelectDriver(index) opens one driver in the
  // inspector; selectedDriverIndex highlights the open row. Both omitted on the print path.
  onSelectDriver: PropTypes.func,
  selectedDriverIndex: PropTypes.number,
};

export default DriverTableBlock;
